import React, { useEffect, useState } from 'react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, PieChart, Pie, Cell, LineChart, Line } from 'recharts';
import api from '../services/api';
import Header from '../components/common/Header';
import Sidebar from '../components/common/Sidebar';

const COLORS = ['#4f46e5', '#14b8a6', '#7c3aed', '#10b981'];

function Dashboard() {
  const [stats, setStats] = useState([]);
  const [charts, setCharts] = useState({ deliveryTrend: [], leadershipMix: [], impactMetrics: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [statsRes, chartsRes] = await Promise.all([
          api.get('/dashboard/stats'),
          api.get('/dashboard/charts')
        ]);
        setStats(Array.isArray(statsRes.data?.stats) ? statsRes.data.stats : []);
        setCharts(chartsRes.data || { deliveryTrend: [], leadershipMix: [], impactMetrics: [] });
      } catch (error) {
        console.error('Failed to load dashboard', error);
        setStats([]);
        setCharts({ deliveryTrend: [], leadershipMix: [], impactMetrics: [] });
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  if (loading) {
    return <div className="loading-screen">Loading executive insights...</div>;
  }

  return (
    <div className="app-shell">
      <Header brand="HASEEB AHMED — EXECUTIVE INSIGHTS" links={[{ label: 'Back to Profile', href: '/' }]} />

      <div className="dashboard-layout">
        <Sidebar />

        <section className="dashboard-content">
          <div className="container">
            <h2 className="section-title">Executive Snapshot Dashboard</h2>
            <p className="section-subtitle">A concise analytics view presenting leadership profile impact, delivery trajectory, and competency composition.</p>

            <div className="dashboard-grid">
              {stats.map((stat, index) => (
                <div className="dashboard-stat" key={index}>
                  <div className="dashboard-stat-value">{stat.value}</div>
                  <div className="dashboard-stat-label">{stat.label}</div>
                </div>
              ))}
            </div>

            <div className="charts-grid">
              <div className="chart-card" id="delivery-trend">
                <h3>Delivery Performance Trend</h3>
                <ResponsiveContainer width="100%" height={320}>
                  <LineChart data={charts.deliveryTrend}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Line type="monotone" dataKey="value" stroke="#4f46e5" strokeWidth={3} />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              <div className="chart-card">
                <h3>Competency Distribution</h3>
                <ResponsiveContainer width="100%" height={320}>
                  <PieChart>
                    <Pie data={charts.leadershipMix} dataKey="value" nameKey="name" outerRadius={110} label>
                      {charts.leadershipMix.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              <div className="chart-card" id="impact-metrics" style={{ gridColumn: '1 / -1' }}>
                <h3>Business Impact Metrics</h3>
                <ResponsiveContainer width="100%" height={320}>
                  <BarChart data={charts.impactMetrics}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="value" fill="#14b8a6" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

export default Dashboard;
